import React, { useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import { NumericFormat as NumberFormat } from 'react-number-format';
import { useQueryUserConfig } from '@src/queries/users/useQueryUserConfig';
import * as appHelpers from '@src/services/helpers/appHelpers';
import { StringHelper } from '@src/services/helpers/string';

import { setPopup } from '@src/state/popupAtoms';
import { CollectionView } from 'common';
import { api } from '@src/services/api';
import { EPopup } from '@src/types/common';
import { PageTitle } from '@src/components/PageTitle';

import { Pathname, Browsing, ArgsName, PathArgs } from '@src/types/Browsing';
import { queryClient, QUERY_KEYS } from '@src/query';
import { useMutationCollectionDelete } from '@src/queries/collections/useMutationCollectionDelete';
import { useMutationCollectionDuplicate } from '@src/queries/collections/useMutationCollectionDuplicate';
import { getCollectionLangError } from '@src/pages/Collection/collectionErrors';
import { RightsRequestStatusSelect, RightsRequestStatusValue } from '@src/components/RightsRequestStatusSelect';
import { stylesheet } from 'typestyle';
import DownloadIcon from '@mui/icons-material/GetApp';
import ShareIcon from '@mui/icons-material/Share';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/Delete';
import CopyIcon from '@mui/icons-material/FileCopy';
import PinIcon from '@mui/icons-material/Pin';
import LiveIcon from '@mui/icons-material/FiberManualRecord';
import SyncIcon from '@mui/icons-material/Sync';
import { ADALONG_COLORS } from '@src/styles';
import { LocalizedStrings } from '@src/utils/localizedStrings';
import { ActionButton } from '@src/components/ActionButton';
import { BulkMediaActions } from '@src/components/BulkMediaActions';
import { WithMediaSelection } from '@src/types/MediaSelection';
import { MediaActionButtonController } from '@src/fragments/MediaActionButton/MediaActionController';
import { IPopupsService } from '@src/types/Popups';
import { PermissionSettings } from '@src/pages/Collection/View/CollectionHeader/components/PermissionSettings';
import UserAccessList from '@src/pages/Collection/View/CollectionHeader/components/PermissionList/UserAccessList';
import {
  useMutationCollectionLock,
  useMutationCollectionSetAsFavorite,
  useMutationCollectionUpdateQuery,
} from '@src/queries/collections/useMutationCollection';
import { Lock, LockOpen, Star, StarBorder, Tune } from '@mui/icons-material';
import { useAddAlert } from '@src/fragments/Alerting/alertAtom';
import { getCollection } from '@src/queries/collections/api';
import { CollectionSettings } from './components/CollectionSettings';
import { ButtonIcon } from '@src/components/ButtonIcon';

interface Props extends WithMediaSelection {
  isLoading: boolean;
  collection: CollectionView | null;
  totalResults: number;
  filtersOpened: boolean;
  onToggleFilters: () => void;
  rightsStatusFilter: RightsRequestStatusValue;
  services: {
    popups: IPopupsService;
    browsing: Browsing;
  };
  setRightsStatusFilters: (status: RightsRequestStatusValue) => void;
}

export function CollectionHeader(props: Props) {
  const [settings, setSettings] = useState(false);
  const [permission, setPermission] = useState(false);
  const [share, setShare] = useState<boolean | string>();
  const [copied, setCopied] = useState(false);
  const addAlert = useAddAlert();

  const { mutateAsync: lockCollection, reset: resetLock } = useMutationCollectionLock();
  const { mutateAsync: setAsFavorite, reset: resetSetAsFavorite } = useMutationCollectionSetAsFavorite();
  const {
    mutateAsync: updateCollectionQuery,
    reset: resetUpdateQuery,
    isPending: isForceSyncing,
  } = useMutationCollectionUpdateQuery();
  const { mutateAsync: deleteCollection, reset: resetDelete } = useMutationCollectionDelete();
  const { mutateAsync: duplicateCollection, reset: resetDuplicate } = useMutationCollectionDuplicate();

  const { data: userConfig } = useQueryUserConfig();
  const user = userConfig?.user ?? null;
  const onCollectionLock = () => {
    if (!props.collection) {
      return;
    }
    const nextLockedState = !(props.collection.locked ?? false);
    lockCollection(
      {
        _id: props.collection._id,
        locked: nextLockedState,
      },
      {
        onSuccess: () => {
          appHelpers.addAlert({
            text: nextLockedState ? 'Collection locked.' : 'Collection unlocked.',
            type: 'success',
          });
        },
        onError: (error) =>
          addAlert({
            text: getCollectionLangError(error),
            close: resetLock,
          }),
      }
    ).catch(console.error);
  };

  const onSetAsMain = () => {
    if (!props.collection) {
      return;
    }
    const nextIsFavorite = !(props.collection.isFavorite ?? false);
    setAsFavorite(
      {
        collectionId: props.collection._id,
        isFavorite: nextIsFavorite,
      },
      {
        onSuccess: () => {
          appHelpers.addAlert({
            text: nextIsFavorite ? lang.buttons.setAsFavoriteSuccess : lang.buttons.unsetAsFavoriteSuccess,
            type: 'success',
          });
        },
        onError: (error) =>
          addAlert({
            text: getCollectionLangError(error),
            close: resetSetAsFavorite,
          }),
      }
    ).catch(console.error);
  };

  const onForceSyncWithDiscover = async () => {
    if (!props.collection) {
      return;
    }
    if (props.collection.locked) {
      appHelpers.addAlert({ text: 'Collection is locked!', type: 'warning' });
      return;
    }

    try {
      const fullCollection = await getCollection(props.collection._id, true);
      await updateCollectionQuery(
        {
          collectionId: props.collection._id,
          query: fullCollection.query || null,
          needs_reset: true,
          reset_in_progress: false,
        },
        {
          onSuccess: () => {
            appHelpers.addAlert({
              text: lang.buttons.forceSyncSuccess,
              type: 'success',
            });
          },
          onError: (error) =>
            addAlert({
              text: getCollectionLangError(error),
              close: resetUpdateQuery,
            }),
        }
      );
    } catch (error) {
      addAlert({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
        text: getCollectionLangError((error as any) ?? null),
        close: resetUpdateQuery,
      });
    }
  };

  const onCollectionSettingsClose = () => {
    setSettings(false);
  };

  const onCollectionSettings = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (props.collection?.locked) {
      appHelpers.addAlert({ text: 'Collection is locked!', type: 'warning' });
      return;
    }
    e.stopPropagation();
    setSettings(true);
  };

  const onPermissionSettings = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    setPermission(true);
  };

  const onPermissionsClose = () => {
    setPermission(false);
  };

  const onDownload = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    if (props.collection) {
      setPopup(EPopup.downloadCollection, props.collection._id);
    }
  };

  const onClickShareOverlay = () => {
    setShare(false);
  };

  const onShare = () => {
    if (!props.collection) {
      return;
    }

    api
      .getCollectionShare(props.collection._id)
      .then((token: string) => {
        if (props.collection) {
          setShare(`${window.location.origin}/collections/${props.collection._id}?t=${token}`);
        }
      })
      .catch((err: unknown) => {
        const errorType = err instanceof Error ? err.message : 'unknown';
        appHelpers.addAlert({ text: `common.error.${errorType}`, type: 'danger' });
      });
  };

  const closeShare = (): void => {
    setShare(undefined);
  };

  const onDelete = () => {
    if (props.collection?.locked) {
      appHelpers.addAlert({ text: 'Collection is locked!', type: 'warning' });
      return;
    }
    if (props.collection) {
      setPopup(EPopup.promptconfirm, {
        question: window.T('create.collections.confirmremove'),
        text: { yes: 'DELETE', no: 'CANCEL' },
        yesColor: 'red',
        answer: (yes) => {
          if (!yes || !props.collection) {
            return;
          }
          deleteCollection(
            {
              collectionId: props.collection._id,
            },
            {
              onSuccess: () => {
                setTimeout(() => appHelpers.setRoute(Pathname.collect), 100);
              },

              onError: (error) =>
                addAlert({
                  text: getCollectionLangError(error),
                  close: resetDelete,
                }),
            }
          ).catch(console.error);
        },
      });
    }
  };

  const onRightsStatus = (val: RightsRequestStatusValue) => {
    props.setRightsStatusFilters(val);
    const collectionId = props.collection?._id;
    if (collectionId) {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.collection(collectionId) });
    }
  };

  const onDuplicate = () => {
    if (props.collection) {
      setPopup(EPopup.promptconfirm, {
        question: window.T('create.collection.confirmduplicate'),
        answer: (yes) => {
          if (!yes || !props.collection) {
            return;
          }
          duplicateCollection(
            {
              collectionId: props.collection._id,
            },
            {
              onSuccess: (newCol) => {
                appHelpers.setRoute(`/collections/${newCol._id}` as Pathname);
              },
              onError: (error) =>
                addAlert({
                  text: getCollectionLangError(error),
                  close: resetDuplicate,
                }),
            }
          ).catch(console.error);
        },
      });
    }
  };

  const { collection, isLoading } = props;
  const permissions = collection?.permissions?.filter((p) => !!p.user) || [];

  if (isLoading) {
    return null;
  }

  if (!collection) {
    return <div className='header'></div>;
  }

  const shareStyle = { display: 'flex' };
  const isSyncableCollection = collection.type === 'auto';
  const onStatusClick = (widgetId: string[]) => {
    if (widgetId.length === 1) {
      const args: PathArgs = new Map();
      args.set(ArgsName.widget.id, widgetId[0]);
      props.services.browsing.goToPage({
        root: Pathname.widget,
        args,
      });
    }
  };

  // Button config array (type-safe)
  type ActionButtonConfig = {
    key: string;
    icon: React.ReactNode;
    label: string;
    onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    title: string;
    style?: React.CSSProperties;
    disabled?: boolean;
  };

  const actionButtons: ActionButtonConfig[] = [
    ...(user
      ? [
        {
          key: 'filters',
          icon: <Tune style={{ fontSize: 16 }} />,
          label: lang.buttons.filters,
          onClick: props.onToggleFilters,
          title: props.filtersOpened ? lang.buttons.hideFilters : lang.buttons.showFilters,
        },
      ]
      : []),
    {
      key: 'key',
      icon: <PinIcon style={{ fontSize: 16 }} />,
      label: copied ? 'COPIED!' : 'Copy ID',
      onClick: () => {
        void navigator.clipboard.writeText(collection._id);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      title: 'Copy collection ID',
    },
    ...(collection.mine
      ? [
        {
          key: 'main',
          icon: collection.isFavorite ? <Star style={{ fontSize: 16, color: '#f5a623' }} /> : <StarBorder style={{ fontSize: 16 }} />,
          label: collection.isFavorite ? lang.buttons.unsetAsFavorite : lang.buttons.setAsFavorite,
          onClick: onSetAsMain,
          title: collection.isFavorite ? lang.buttons.unsetAsFavorite : lang.buttons.setAsFavorite,
        },
        {
          key: 'lock',
          icon: collection.locked ? <LockOpen style={{ fontSize: 16 }} /> : <Lock style={{ fontSize: 16 }} />,
          label: collection.locked ? lang.buttons.unlock : lang.buttons.lock,
          onClick: onCollectionLock,
          title: collection.locked ? lang.buttons.unlock : lang.buttons.lock,
          style: { opacity: collection.locked ? 0.5 : 1 },
        },
      ]
      : []),
    {
      key: 'duplicate',
      icon: <CopyIcon style={{ fontSize: 16 }} />,
      label: lang.buttons.copy,
      onClick: onDuplicate,
      title: lang.buttons.copy,
    },
    {
      key: 'share',
      icon: <ShareIcon style={{ fontSize: 16 }} />,
      label: lang.buttons.share,
      onClick: onShare,
      title: lang.buttons.share,
    },
    {
      key: 'download',
      icon: <DownloadIcon style={{ fontSize: 16 }} />,
      label: lang.buttons.download,
      onClick: onDownload,
      title: lang.buttons.download,
    },
    ...(collection.i_can_edit && isSyncableCollection
      ? [
        {
          key: 'force-sync',
          icon: <SyncIcon style={{ fontSize: 16 }} />,
          label: isForceSyncing ? lang.buttons.forceSyncLoading : lang.buttons.forceSync,
          onClick: onForceSyncWithDiscover,
          title: lang.buttons.forceSyncTooltip,
          style: { opacity: collection.locked || isForceSyncing ? 0.5 : 1 },
          disabled: collection.locked || isForceSyncing,
        },
      ]
      : []),
    ...(collection.mine
      ? [
        {
          key: 'settings',
          icon: <SettingsIcon style={{ fontSize: 16 }} />,
          label: lang.buttons.settings,
          onClick: onCollectionSettings,
          title: lang.buttons.settings,
          style: { opacity: collection.locked ? 0.5 : 1 },
        },
        {
          key: 'delete',
          icon: <DeleteIcon style={{ fontSize: 16 }} />,
          label: lang.buttons.delete,
          onClick: onDelete,
          title: lang.buttons.delete,
          style: { opacity: collection.locked ? 0.5 : 1 },
        },
      ]
      : []),
  ];

  return (
    <div className={sheet.header}>
      <div className={sheet.left}>
        <div className={sheet.title}>
          <PageTitle>
            {collection.live_widgets && collection.live_widgets.ids.length > 0 ? (
              <ButtonIcon
                icon={LiveIcon}
                titleAccess={collection.live_widgets.names.join(' • ')}
                className={sheet.liveIcon}
                onClick={() => onStatusClick(collection.live_widgets!.ids)}
                sx={{ color: 'green', fontSize: 20, '&:hover': { color: 'darkgreen' } }}
              />
            ) : null}
            {StringHelper.upperFirst(collection.name)}
          </PageTitle>
          {user ? (
            <div className={sheet.userOwnerRow}>
              <UserAccessList permissionList={permissions} onClick={onPermissionSettings} isMine={collection.mine} />
              <span className={sheet.ownerSubtle}>
                {collection.mine && window.T('create.collection.selfowner')}
                {!collection.mine && window.T('create.collection.owner', { owner: collection.owner.displayName })}
              </span>
            </div>
          ) : null}
        </div>
        <div className={sheet.desc}>{StringHelper.upperFirst(collection.description)}</div>

        <div className={sheet.toolbar}>
          {user ? (
            <BulkMediaActions
              count={props.mediaSelection.length}
              onClear={() => props.setMediaSelection([])}
              mediaActionButton={
                <MediaActionButtonController
                  inCollection={collection}
                  mediaList={props.mediaSelection}
                  services={props.services}
                  catalogCountries={user.catalogCountries}
                />
              }
            />
          ) : null}
        </div>
      </div>
      <div className={sheet.right}>
        <div className={sheet.actionButtonRow}>
          {actionButtons.map((btn) => (
            <div className={sheet.animatedButtonWrapper} key={btn.key}>
              <Tooltip title={btn.title} placement='bottom'>
                <span>
                  <ActionButton
                    variant="transparent"
                    size="small"
                    minWidth={0}
                    onAction={btn.onClick}
                    className={sheet.animatedButton}
                    title={btn.title}
                    style={btn.style}
                    disabled={btn.disabled}
                  >
                    <span className={sheet.iconWithText}>
                      {btn.icon}
                      <span className={sheet.buttonText}>{btn.label}</span>
                    </span>
                  </ActionButton>
                </span>
              </Tooltip>
            </div>
          ))}
        </div>

        <div className={sheet.filters}>
          <div className={sheet.count}>
            <NumberFormat value={props.totalResults} displayType='text' thousandSeparator /> elements
          </div>
          {!user ? null : (
            <RightsRequestStatusSelect onChange={onRightsStatus} value={props.rightsStatusFilter} />
          )}
        </div>

        {!settings ? null : <CollectionSettings collection={collection} onClose={() => onCollectionSettingsClose()} />}
        {!permission ? null : <PermissionSettings collection={collection} onClose={() => onPermissionsClose()} />}
      </div>
      {share ? (
        <div className='share-overlay' onClick={onClickShareOverlay}>
          <div onClick={(e) => e.stopPropagation()}>
            <div className='title' style={shareStyle}>
              <div className='desc'>{window.T('create.collection.share')}</div>
              <div className='remove' onClick={closeShare}>
                <i className='material-icons notranslate' style={{ color: '#adadad' }}>
                  close
                </i>
              </div>
            </div>
            <div className='link'>{share}</div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const lang = LocalizedStrings({
  en: {
    buttons: {
      lock: 'Lock from editing',
      unlock: 'Unlock and allow editing',
      setAsFavorite: 'Set as favorite',
      unsetAsFavorite: 'Unset as favorite',
      setAsFavoriteSuccess: 'Collection set as favorite.',
      unsetAsFavoriteSuccess: 'Collection unset as favorite.',
      permission: 'Check permissions',
      delete: 'Delete this collection',
      copy: 'Duplicate collection',
      filters: 'Filters',
      showFilters: 'Show filters',
      hideFilters: 'Hide filters',
      settings: 'Settings',
      forceSync: 'Force sync with discover',
      forceSyncLoading: 'Syncing...',
      forceSyncSuccess: 'Collection scheduled for discover sync.',
      forceSyncTooltip: 'Use this if you want to force sync this collection with the discover collection filter. It will be synced within the next 24 hours.',
      download: "Download",
      share: 'Share',
    },
  },
});


// Refactored header stylesheet for clarity and maintainability
const HEADER_GAP = 12;
const DESC_FONT_SIZE = 14;
const COUNT_FONT_SIZE = 13;
const OWNER_FONT_SIZE = 13;
const BUTTON_TEXT_FONT_SIZE = 13;

const sheet = stylesheet({
  userOwnerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: HEADER_GAP,
    margin: '10px 15px',
    height: 'auto',
    $nest: {
      '@media (max-width: 900px)': {
        flexDirection: 'column',
      },
    },
  },
  left: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    gap: 2,
    flex: '1 1 auto',
    minWidth: 0,
  },
  right: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    flex: '0 0 auto',
    minWidth: 0,
    maxWidth: '50%',
    $nest: {
      '@media (max-width: 900px)': {
        width: '100%',
        maxWidth: 'none',
      },
    },
  },
  title: {
    display: 'flex',
    gap: HEADER_GAP,
    position: 'relative',
  },
  desc: {
    fontSize: DESC_FONT_SIZE,
    color: ADALONG_COLORS.DARK_GRAY,
    position: 'relative',
  },
  count: {
    fontSize: COUNT_FONT_SIZE,
    color: ADALONG_COLORS.DARK_GRAY,
  },
  ownerSubtle: {
    fontSize: OWNER_FONT_SIZE,
    color: ADALONG_COLORS.LIGHT_GRAY,
    marginLeft: 6,
    fontWeight: 400,
    letterSpacing: 0.1,
    verticalAlign: 'middle',
  },
  actionButtonRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    minHeight: 32,
    margin: '10px 0',
  },
  animatedButtonWrapper: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    overflow: 'visible',
  },
  animatedButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: 'auto',
    minWidth: 0,
    padding: '2px 8px',
    background: 'transparent',
    cursor: 'pointer',
    border: 'none',
    overflow: 'hidden',
    position: 'relative',
    transition: 'width 0.2s cubic-bezier(0.4,0,0.2,1), background 0.2s cubic-bezier(0.4,0,0.2,1)',
    $nest: {
      '&:hover': {
        background: '#f5f5f5',
        zIndex: 2,
      },
      '&:hover span': {
        opacity: 1,
        maxWidth: 400,
        pointerEvents: 'auto',
      },
    },
  },
  iconWithText: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    position: 'relative',
  },
  buttonText: {
    opacity: 0,
    maxWidth: 0,
    minWidth: 0,
    marginLeft: 8,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    display: 'inline-block',
    transition: 'opacity 0.2s, max-width 0.2s, min-width 0.2s',
    pointerEvents: 'none',
    fontSize: BUTTON_TEXT_FONT_SIZE,
    fontWeight: 500,
    color: ADALONG_COLORS.DARK_GRAY,
  },
  liveIcon: {
    marginRight: 8,
  },
  filters: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: '100%',
    gap: 5
  },
  toolbar: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: HEADER_GAP,
    width: '100%',
  },
});
